/**
 * Base error class with rich context for feature errors
 */
export class FeatureError extends Error {
    code;
    details;
    cause;
    constructor(code, message, details, cause) {
        super(message);
        this.code = code;
        this.details = details;
        this.cause = cause;
        this.name = 'FeatureError';
        // Maintains proper stack trace for where error was thrown (V8 engines)
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, FeatureError);
        }
    }
    /**
     * Convert to API-friendly JSON format
     */
    toJSON() {
        return {
            code: this.code,
            message: this.message,
            details: this.details,
        };
    }
}
/**
 * Helper to create a success result
 *
 * @example
 * ```ts
 * return ok(user);
 * ```
 */
export const ok = (data) => ({
    success: true,
    data,
});
/**
 * Helper to create an error result
 *
 * @example
 * ```ts
 * return err(new FeatureError('USER_NOT_FOUND', 'User not found'));
 * ```
 */
export const err = (error) => ({
    success: false,
    error,
});
/**
 * Common error codes used across features
 */
export const ErrorCodes = {
    // Validation errors
    VALIDATION_ERROR: 'VALIDATION_ERROR',
    INVALID_INPUT: 'INVALID_INPUT',
    // Resource errors
    NOT_FOUND: 'NOT_FOUND',
    ALREADY_EXISTS: 'ALREADY_EXISTS',
    CONFLICT: 'CONFLICT',
    INVALID_STATE: 'INVALID_STATE',
    /**
     * The asset being edited is still being rendered, so there is nothing to
     * anchor the edit to yet. Transient, unlike INVALID_STATE: the caller retries
     * after `details.retryAfterSeconds`. Kept separate so a tool can relay "wait
     * N seconds" rather than the generic "cannot be edited" text (ENG-1950).
     */
    RENDER_IN_PROGRESS: 'RENDER_IN_PROGRESS',
    // Authorization errors
    UNAUTHORIZED: 'UNAUTHORIZED',
    FORBIDDEN: 'FORBIDDEN',
    /**
     * A capability the caller asked for is not configured on this deployment
     * (e.g. the CDN is switched off). NOT the same as FORBIDDEN — the caller is
     * allowed, the feature simply isn't wired up here — and it must not share
     * FORBIDDEN's 403, which the frontend reads as a permission failure.
     */
    NOT_CONFIGURED: 'NOT_CONFIGURED',
    // Database errors
    DATABASE_ERROR: 'DATABASE_ERROR',
    TRANSACTION_FAILED: 'TRANSACTION_FAILED',
    // External service errors
    EXTERNAL_SERVICE_ERROR: 'EXTERNAL_SERVICE_ERROR',
    TIMEOUT: 'TIMEOUT',
    RATE_LIMITED: 'RATE_LIMITED',
    // AI generation errors (Track 19)
    NO_AI_ELIGIBLE_TEMPLATES: 'NO_AI_ELIGIBLE_TEMPLATES',
    RETRIEVAL_EMPTY: 'RETRIEVAL_EMPTY',
    AI_VALIDATION_FAILED: 'AI_VALIDATION_FAILED',
    AI_RATE_LIMITED: 'AI_RATE_LIMITED',
    AI_MODEL_REFUSED: 'AI_MODEL_REFUSED',
    /**
     * An image provider's safety system (OpenAI moderation, Gemini safety
     * filters) blocked the generation. Terminal: re-sending the same request is
     * blocked again, so callers must not retry it automatically. Distinct from
     * AI_MODEL_REFUSED, which also covers "returned no image" soft failures.
     */
    AI_SAFETY_BLOCKED: 'AI_SAFETY_BLOCKED',
    COST_CAP_EXCEEDED: 'COST_CAP_EXCEEDED',
    TEMPLATE_NOT_AI_ELIGIBLE: 'TEMPLATE_NOT_AI_ELIGIBLE',
    // Generic errors
    INTERNAL_ERROR: 'INTERNAL_ERROR',
    UNKNOWN_ERROR: 'UNKNOWN_ERROR',
    /**
     * The connection pool refused to admit this request before opening a
     * transaction (packages/database/src/admission.ts's DbAdmissionTimeoutError)
     * -- a capacity signal, not a failed write. Safe to retry verbatim, and
     * must not page on-call the way INTERNAL_ERROR does. Maps to HTTP 503.
     */
    SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
};
/**
 * Detect a Postgres exclusion-constraint violation (SQLSTATE 23P01), optionally
 * for a specific constraint name. Used to turn a lost double-booking race (the
 * `appointment_no_overlap` GiST exclusion constraint firing on a concurrent
 * INSERT) into a clean CONFLICT rather than a 500. Drizzle wraps the driver
 * error, so we walk the `cause` chain.
 */
export const isExclusionViolation = (error, constraintName) => {
    let current = error;
    for (let depth = 0; depth < 5 && current; depth++) {
        const e = current;
        if (e.code === '23P01') {
            const name = e.constraint_name ?? e.constraint;
            if (!constraintName || name === constraintName)
                return true;
        }
        current = e.cause;
    }
    return false;
};
/**
 * True when `error` (or something in its `cause` chain) is a
 * `DbAdmissionTimeoutError` -- the connection pool refusing to admit a request
 * before opening a transaction. Matched STRUCTURALLY by `.name` rather than
 * `instanceof`, because this package cannot import
 * `@borradh-workspace/database` (the admission timeout is thrown on the
 * database side, and the name is pinned there too -- see
 * rls-admission.test.ts) and because drizzle re-wraps thrown errors in its own
 * "Failed query: …" error with the original on `cause`.
 */
export const isAdmissionTimeoutError = (error) => {
    let current = error;
    for (let depth = 0; depth < 5 && current; depth++) {
        if (current instanceof Error &&
            current.name === 'DbAdmissionTimeoutError') {
            return true;
        }
        current = current instanceof Error ? current.cause : undefined;
    }
    return false;
};
/**
 * Create a sanitized internal error result.
 * Use this in catch blocks to avoid exposing internal error details to clients.
 * The original error is preserved in the `cause` property for server-side logging.
 *
 * An admission timeout is re-coded to SERVICE_UNAVAILABLE rather than left as
 * INTERNAL_ERROR: it is a capacity signal the caller can retry verbatim (the
 * request was refused before any transaction opened), not a failed write, and
 * it must not page on-call the way a real INTERNAL_ERROR does.
 *
 * @param fallbackMessage - User-friendly message to show (default: generic message)
 * @param error - The caught error (preserved in cause for logging)
 */
export const internalError = (fallbackMessage = 'An unexpected error occurred. Please try again.', error) => {
    const cause = error instanceof Error ? error : undefined;
    if (isAdmissionTimeoutError(error)) {
        return err(new FeatureError(ErrorCodes.SERVICE_UNAVAILABLE, 'The service is temporarily at capacity. Please try again.', undefined, cause));
    }
    return err(new FeatureError(ErrorCodes.INTERNAL_ERROR, fallbackMessage, undefined, cause));
};
//# sourceMappingURL=errors.js.map